import { motion } from "framer-motion";
import { Phone, Star, MapPin, Wallet } from "lucide-react";
import { PHONE, PHONE_LINK, ADDRESS, isOpenNow } from "../data/menu";

const Line = ({ children, delay, className }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className={`block ${className || ""}`}
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.33, 1, 0.68, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const open = isOpenNow();
  return (
    <section id="top" data-testid="hero-section" className="relative z-10 flex min-h-[92vh] items-center pt-28 pb-16">
      <div className="mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-14 px-5 md:px-10 lg:grid-cols-12">
        <div className="lg:col-span-7">
        <motion.img
          src="/assets/logo.jpg"
          alt="Imbiss Tsolias Logo"
          data-testid="hero-logo"
          initial={{ opacity: 0, scale: 0.9, rotate: -5 }}
          animate={{ opacity: 1, scale: 1, rotate: -2 }}
          transition={{ duration: 0.9, ease: [0.33, 1, 0.68, 1] }}
          className="mb-10 h-36 w-auto rounded-2xl border border-[#E2E0D8] bg-white p-4 shadow-[0_24px_60px_rgba(26,28,25,0.14)] sm:h-44 md:h-56"
        />

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="mb-6 text-xs font-bold uppercase tracking-[0.28em] text-[#78825B]"
          data-testid="hero-eyebrow"
        >
          Griechischer Imbiss · Siegen
        </motion.p>

        <h1 className="font-display text-[17vw] font-normal leading-[0.9] tracking-tighter text-[#1A1C19] sm:text-[13vw] lg:text-[9.5rem]">
          <Line delay={0.25}>Imbiss</Line>
          <Line delay={0.4} className="italic text-[#4B7F94]">Tsolias</Line>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.7, ease: "easeOut" }}
          className="mt-8 max-w-lg text-lg leading-relaxed text-[#3D403A] md:text-xl"
          data-testid="hero-tagline"
        >
          Griechische Spezialitäten wie Gyros und Co. — seit 1985 in Siegen.
          Familiär mit Liebe zubereitet.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.7, ease: "easeOut" }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href={PHONE_LINK}
            data-testid="hero-call-button"
            className="flex items-center gap-3 rounded-full bg-[#1A1C19] px-8 py-4 text-sm font-semibold tracking-wide text-[#F9F8F6] transition-all duration-300 hover:-translate-y-1 hover:bg-[#4B7F94]"
          >
            <Phone size={16} strokeWidth={1.5} />
            {PHONE} anrufen
          </a>
          <a
            href="#speisekarte"
            data-testid="hero-menu-button"
            className="rounded-full border border-[#1A1C19]/25 px-8 py-4 text-sm font-semibold tracking-wide text-[#1A1C19] transition-all duration-300 hover:-translate-y-1 hover:border-[#4B7F94] hover:text-[#4B7F94]"
          >
            Speisekarte ansehen
          </a>
          <span
            data-testid="hero-open-badge"
            className="flex items-center gap-2 rounded-full border border-[#E2E0D8] bg-white/60 px-5 py-3 text-xs font-semibold uppercase tracking-[0.15em] text-[#4A4D46] backdrop-blur-sm"
          >
            <span className={`pulse-dot h-2 w-2 rounded-full ${open ? "bg-[#78825B]" : "bg-[#C9A227]"}`} />
            {open ? "Jetzt geöffnet" : "Zurzeit geschlossen"}
          </span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.3, duration: 0.8 }}
          className="mt-16 flex flex-wrap gap-x-10 gap-y-4 border-t border-[#E2E0D8] pt-6 text-sm text-[#4A4D46]"
        >
          <span className="flex items-center gap-2" data-testid="hero-rating-chip">
            <Star size={15} strokeWidth={1.5} className="fill-[#C9A227] text-[#C9A227]" />
            4,5 / 5 · 1.220+ Google-Rezensionen
          </span>
          <span className="flex items-center gap-2" data-testid="hero-price-chip">
            <Wallet size={15} strokeWidth={1.5} />
            10–20 € pro Person
          </span>
          <span className="flex items-center gap-2" data-testid="hero-address-chip">
            <MapPin size={15} strokeWidth={1.5} />
            {ADDRESS}
          </span>
        </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40, rotate: 2 }}
          animate={{ opacity: 1, y: 0, rotate: 0 }}
          transition={{ duration: 1, delay: 0.5, ease: [0.33, 1, 0.68, 1] }}
          className="relative lg:col-span-5"
          data-testid="hero-room"
        >
          <div className="overflow-hidden rounded-2xl border border-[#E2E0D8] shadow-[0_32px_80px_rgba(26,28,25,0.14)]">
            <img
              src="/assets/raum.jpg"
              alt="Innenraum des Imbiss Tsolias in Siegen"
              data-testid="hero-room-image"
              className="h-[38vh] w-full object-cover lg:h-[62vh]"
            />
          </div>
          <p className="mt-4 text-xs font-semibold uppercase tracking-[0.2em] text-[#78825B]">
            Unser Raum · Spandauer Str. 1, Siegen
          </p>
        </motion.div>
      </div>
    </section>
  );
};
